/* eslint-disable class-methods-use-this */
import { WebAuth, Authentication } from 'auth0-js';

// app modules: services
import { sendEvent } from 'services/analytics';
import history from 'services/history';

export default class Auth {
  constructor(params) {
    this.params = params;

    this.apiUrl = new URL(params.apiUrl);

    let audience;
    try {
      audience = atob(params.apiAudience);
    } catch (err) {
      audience = params.apiAudience;
    }

    this.auth0 = new WebAuth({
      domain: this.apiUrl.hostname,
      clientID: params.clientId,
      audience,
      redirectUri: params.callbackUrl,
      scope: params.scope,
      responseType: params.responseType || 'code',
      responseMode: params.responseMode || 'form_post',
    });
    this.client = new Authentication(this.auth0, {
      clientID: params.clientId,
      domain: this.apiUrl.hostname,
    });
  }

  static generateNonce() {
    const result = [];
    if (typeof window.crypto !== 'undefined') {
      const charset = '0123456789ABCDEFGHIJKLMNOPQRSTUVXYZabcdefghijklmnopqrstuvwxyz-._~';
      window.crypto
        .getRandomValues(new Uint8Array(32))
        .forEach(c => result.push(charset[c % charset.length]));
    }
    return result.join('');
  }

  static createState(options) {
    // The nonce should be checked after the redirect to avoid csrf attacks
    const { search, state } = history.location;
    const returnToParam = options.returnTo || state?.returnTo;
    let returnTo;
    if (typeof returnToParam === 'object') {
      returnTo = `${returnToParam.pathname || '/'}${returnToParam.search || ''}`;
    } else if (typeof returnToParam === 'string') {
      returnTo = returnToParam;
    } else {
      returnTo = `/${search}`;
    }
    return JSON.stringify({
      returnTo,
      nonce: Auth.generateNonce(),
      // We send the host so that we can detect EZProxy logins and use the correct callback URL serverside.
      // The value will be checked against a whitelist.
      // We base64 encode to avoid EZProxy rewriting it
      host: window.btoa(window.location.host),
    });
  }

  signupByEmail(email, password, metadata, returnTo) {
    return new Promise((resolve, reject) => {
      try {
        this.auth0.signup(
          {
            email,
            password,
            user_metadata: metadata,
            connection: this.params.realm,
          },
          err => {
            if (err) {
              console.error(err);
              sendEvent({
                event: 'Auth.RegistrationFailed',
                method: 'email',
                err,
              });
              if (
                err.code === 'extensibility_error' &&
                err.original &&
                err.original.response &&
                err.original.response.body &&
                err.original.response.body.friendly_message
              ) {
                // Return the friendly message sent by our Auth0 hook
                reject(new Error(err.original.response.body.friendly_message));
              }
              return reject(err);
            }
            sendEvent({
              event: 'Auth.RegistrationComplete',
              method: 'email',
            });
            return this.signin(email, password, returnTo)
              .then(resolve)
              .catch(reject);
          },
        );
      } catch (err) {
        reject(err);
      }
    });
  }

  socialSignin(provider, returnTo) {
    return new Promise((resolve, reject) => {
      try {
        this.auth0.authorize(
          {
            connection: provider,
            prompt: 'popup',
            state: Auth.createState({ returnTo }),
          },
          (err, authResult) => {
            if (err) {
              console.error(err);
              return reject(err);
            }
            return resolve(authResult);
          },
        );
      } catch (err) {
        reject(err);
      }
    });
  }

  verifyPassword(username, password) {
    return new Promise((resolve, reject) => {
      try {
        this.client.login({ realm: this.params.realm, username, password }, (err, authResult) => {
          if (err) {
            console.error(err);
            return reject(err);
          }
          return resolve(authResult);
        });
      } catch (err) {
        reject(err);
      }
    });
  }

  signin(username, password, returnTo) {
    return new Promise((resolve, reject) => {
      try {
        this.auth0.login(
          { realm: this.params.realm, state: Auth.createState({ returnTo }), username, password },
          (err, authResult) => {
            if (err) {
              console.error(err);
              return reject(err);
            }
            return resolve(authResult);
          },
        );
      } catch (err) {
        reject(err);
      }
    });
  }
}
